import { PaymentScheduleApi } from '@app/services/apiServices/PaymentScheduleApi';
import { NetworkErrorService } from "@app/services/NetworkError.service";
import { SpinnerService } from '@app/services/spinner.service';
import { PaymentSchedule } from "@app/models/PaymentSchedule";
import { Property } from "@app/models/Property";
import { PropertyApi } from "@app/services/apiServices/PropertyApi";
import { ErrorHandlerService } from "@app/services/ErrorHandler.service";
import { concatMap, finalize, take, takeUntil, tap } from "rxjs/operators";
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FuturePayment } from "@app/models/FuturePayment";
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from "@angular/router";
import { OneButtonDialogComponent } from "@app/components/sharedDialogs/oneButtonDialog.component";
import { DatePipe } from "@angular/common";
import { Subject } from "rxjs";


@Component({
    selector: 'app-schedule-edit-day',
    template: require('./scheduleEditDay.component.html'),
    providers: [DatePipe]
})
export class ScheduleEditDayComponent  implements OnInit, OnDestroy {

    paymentScheduleId: number;
    originalFixedDay: number;
    fixedDay: number;
    paymentSchedule: Readonly<PaymentSchedule>;
    property: Readonly<Property>;
    sortedFuturePayments: FuturePayment[] = [];
    daysOfMonth: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31];
    doneLoading: boolean;
    prevFormattedStartDate: string;
    private destroy$ = new Subject<void>();

    constructor(
        @Inject(MatDialog) private dialog: MatDialog,
        @Inject(SpinnerService) private spinnerService: SpinnerService,
        @Inject(PaymentScheduleApi) private paymentScheduleApi: PaymentScheduleApi,
        @Inject(PropertyApi) private propertyApi: PropertyApi,
        @Inject(ErrorHandlerService) private errorHandlerService: ErrorHandlerService,
        @Inject(NetworkErrorService) private networkErrorService: NetworkErrorService,
        @Inject(Router) private router: Router,
        @Inject(ActivatedRoute) private route: ActivatedRoute,
        @Inject(DatePipe) private datePipe: DatePipe) {

        if (this.route.snapshot.params.scheduleId) {
            this.paymentScheduleId = this.route.snapshot.params.scheduleId;
        } else if (!window.__karma__) {
            console.error("No paymentScheduleId");
        }

    }

    ngOnInit() {
        this.spinnerService.showSpinner();
        this.initData();
    }

    refreshData() {
        this.spinnerService.showSpinner();

        try{
            this.paymentScheduleApi.paymentScheduleChangeFixedDayGet(this.paymentScheduleId, this.fixedDay)
                .pipe(take(1)).subscribe({ next: scheduleResp => {

                    this.paymentSchedule = Object.freeze(scheduleResp);
                    this.sortFuturePayments();
                    const propertyId = this.paymentSchedule.propertyId;

                    this.propertyApi.propertiesIdGet(propertyId)
                        .pipe(take(1)).subscribe(propertyResp => {
                            this.property = Object.freeze(propertyResp);
                        });
                }, error: (reason) => {
                    this.networkErrorService.handleNetworkError(reason, true)
                        .pipe(take(1)).subscribe(shouldRetry => {
                            if (shouldRetry === true) {
                                this.refreshData();
                            }
                        }, () => {});
                } });
        } catch(reason) {
            this.networkErrorService.handleNetworkError(reason, true)
                .pipe(take(1)).subscribe({ next: shouldRetry => {
                    if (shouldRetry === true) {
                        this.refreshData();
                    }
                }, error: () => {} })
        }

        this.spinnerService.closeSpinner();
    }

    initData() {

        try {
            this.paymentScheduleApi.paymentSchedulesIdGet(this.paymentScheduleId)
                .pipe(take(1)).subscribe({ next: scheduleResp => {
                    this.paymentSchedule = Object.freeze(scheduleResp);
                    this.sortFuturePayments();
                    this.originalFixedDay = this.paymentSchedule.fixedDay ?
                        this.paymentSchedule.fixedDay :
                        this.paymentSchedule.startDate.day;
                    this.fixedDay = this.originalFixedDay;
                    this.prevFormattedStartDate = this.paymentSchedule.futurePayments[0].date.formattedFullDate;
                    const propertyId = this.paymentSchedule.propertyId;
                    this.propertyApi.propertiesIdGet(propertyId)
                        .pipe(take(1)).subscribe(propertyResp => {
                            this.property = Object.freeze(propertyResp);
                            this.doneLoading = true;
                            this.spinnerService.closeSpinner();
                        });
                }, error: (reason) => {
                    this.networkErrorService.handleNetworkError(reason, true)
                        .pipe(take(1)).subscribe(shouldRetry => {
                            if (shouldRetry === true) {
                                this.initData();
                            }
                        })
                } })
        } catch(reason) {
            this.networkErrorService.handleNetworkError(reason, true)
                .pipe(take(1)).subscribe(shouldRetry => {
                    if (shouldRetry === true) {
                        this.initData();
                    }
                })
        }
    }

    save() {
        const dialogRef = this.dialog.open(OneButtonDialogComponent, {
            disableClose: true,
            data: {
                header: 'Confirm New Payment Date',
                message: `Your next payment date has changed from ${this.prevFormattedStartDate} to ${this.paymentSchedule.futurePayments[0].date.formattedFullDate}`,
                buttonText: 'OK'
            }
        });

        dialogRef.afterClosed().pipe(
            take(1),
            takeUntil(this.destroy$),
            tap(() => this.spinnerService.showSpinner()),
            concatMap(() => this.paymentScheduleApi.paymentScheduleChangeFixedDayPost(
                this.paymentScheduleId,
                this.paymentSchedule,
                this.fixedDay
            )
            ),
            finalize(() => this.spinnerService.closeSpinner())
        ).subscribe({
            next: () => {
                this.router.navigateByUrl('/properties/scheduleEdit/' + this.paymentScheduleId, {
                    state: { scheduleId: this.paymentScheduleId }
                });
            },
            error: (err) => {
                const errMsg = err?.error?.message || undefined;
                this.errorHandlerService.handleNetworkError(err, true, errMsg)
                    .pipe(take(1)).subscribe();
            }
        })
    }

    submitDisabled() {
        return (!this.fixedDay
                || this.fixedDay === this.originalFixedDay
                || !this.paymentScheduleId
        );
    }

    sortFuturePayments() {
        this.sortedFuturePayments = [];

        if (this.paymentSchedule.futurePayments && this.paymentSchedule.futurePayments.length > 0) {
            this.sortedFuturePayments =
                this.paymentSchedule.futurePayments.sort((a,b) => {
                    let yearResult = a.date.year - b.date.year;

                    if (yearResult !== 0) {
                        return yearResult;
                    }

                    let monthResult = a.date.month - b.date.month;

                    if (monthResult !== 0) {
                        return monthResult;
                    }

                    let dayResult = a.date.day - b.date.day;

                    if (dayResult !== 0) {
                        return dayResult;
                    }

                    return a.id - b.id;
                })
        }

    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}

